import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../../../../js/api.js";
import {
  listarEmpresasPaginadas,
  listarEmpresasSimples,
  buscarEmpresaPorNomeCnpj,
} from "./empresaService.js";
import { listarAcompanhamentosMe } from "./acompanhamentoService.js";
import {
  normalizarListaEmpresas,
  montarDescricaoEmpresa,
} from "../utils/empresa.js";
import {
  obterDocumentoUsuarioLogadoPagina,
} from "../utils/session.js";
import {
  NIVEL_CONSULTOR,
  NIVEL_SUPEX,
  empresaFoiCadastradaPeloConsultor,
} from "../utils/permissoes.js";
import {
  normalizarEmpresaAcompanhamentoItem,
  deduplicarAcompanhamentosPorEmpresa,
} from "../mappers/empresaMapper.js";
import { carregarDadosEmpresaPorId } from "../../../services/empresaService.js";
import { cadastroRequerSupex } from "../mappers/supexMapper.js";
import {
  obterTipoProgramaUsuarioLogado,
  obterTipoProgramaExplicitoUsuario,
} from "../utils/tipoPrograma.js";
import { extrairListaDeResposta } from "./acompanhamentoHelpers.js";
import {
  extrairIdItem,
  itemCorrespondeAoTermo,
  itemPertenceAoPrograma,
  resolverCnpjItem,
} from "../utils/itemResolvers.js";

/**
 * Resolve a empresa do contexto atual para o nível empresa.
 * Empresa sempre fica presa ao próprio documento, ignorando
 * query/storage de terceiros.
 *
 * @param {string} token
 * @returns {Promise<object|null>}
 */
export async function resolverEmpresaDoNivelEmpresa(token) {
  const documentoUsuario = obterDocumentoUsuarioLogadoPagina();
  const tipoProgExplicito = obterTipoProgramaExplicitoUsuario();
  const tipoProg = tipoProgExplicito ?? obterTipoProgramaUsuarioLogado();

  let empresaDoUsuario = null;

  // Se o programa logado for TCE (0), tenta obter dados pelo endpoint dedicado do TCE
  if (tipoProg === 0) {
    try {
      const respTce = await axios.get(`${API_BASE_URL}/tce/dados-empresa`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      const dadosTce =
        respTce.data?.empresa ||
        respTce.data?.dados ||
        (Array.isArray(respTce.data) ? respTce.data[0] : respTce.data);
      if (dadosTce && (dadosTce.id || dadosTce.empresa_id || respTce.data?.id)) {
        empresaDoUsuario = {
          ...dadosTce,
          id: dadosTce.id || dadosTce.empresa_id || respTce.data?.id,
          tipo_programa: 0,
          _origem: "tce",
        };
      }
    } catch (_) {}
  } else if (tipoProg === 2 || tipoProg === 1) {
    try {
      const respEmp = await axios.get(`${API_BASE_URL}/tca/dados-empresa`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      const dadosEmp =
        respEmp.data?.empresa ||
        respEmp.data?.dados ||
        (Array.isArray(respEmp.data) ? respEmp.data[0] : respEmp.data);
      if (dadosEmp && (dadosEmp.id || dadosEmp.empresa_id || respEmp.data?.id)) {
        empresaDoUsuario = {
          ...dadosEmp,
          id: dadosEmp.id || dadosEmp.empresa_id || respEmp.data?.id,
          tipo_programa: tipoProg,
          _origem: tipoProg === 2 ? "tca-empregador" : "tca",
        };
      }
    } catch (_) {}
  }

  if (!empresaDoUsuario?.id && documentoUsuario) {
    empresaDoUsuario = await buscarEmpresaPorNomeCnpj(documentoUsuario, token);
  }

  if (!empresaDoUsuario?.id && typeof window !== "undefined") {
    try {
      const rawUser = window.localStorage.getItem("user");
      if (rawUser) {
        const u = JSON.parse(rawUser);
        const empId =
          u.empresa_id || u.empresaId || (u.tipo === "empresa" ? u.id : null);
        if (empId) {
          empresaDoUsuario = {
            ...u,
            id: empId,
            tipo_programa:
              tipoProg === 0 ? 0 : tipoProg === 2 ? 2 : (u.tipo_programa ?? 1),
            _origem:
              tipoProg === 0
                ? "tce"
                : tipoProg === 2
                  ? "tca-empregador"
                  : (u._origem || "tca"),
          };
        }
      }
    } catch (_) {}
  }

  if (!empresaDoUsuario?.id && token) {
    try {
      const payload = jwtDecode(token);
      const jwtEmpId =
        payload?.empresa_id || payload?.id_empresa || payload?.empresaId;
      if (jwtEmpId) {
        empresaDoUsuario = {
          ...payload,
          id: jwtEmpId,
          tipo_programa:
            tipoProg === 0 ? 0 : tipoProg === 2 ? 2 : 1,
          _origem:
            tipoProg === 0
              ? "tce"
              : tipoProg === 2
                ? "tca-empregador"
                : "tca",
        };
      }
    } catch (_) {}
  }

  if (!empresaDoUsuario?.id) return null;

  if (!empresaDoUsuario.cnpj || !empresaDoUsuario.razao_social) {
    try {
      const dadosCompletos = await carregarDadosEmpresaPorId(empresaDoUsuario.id);
      if (dadosCompletos) {
        empresaDoUsuario = {
          ...dadosCompletos,
          ...empresaDoUsuario,
          id: empresaDoUsuario.id || dadosCompletos.id,
          cnpj: empresaDoUsuario.cnpj || dadosCompletos.cnpj || dadosCompletos.documento || "",
          razao_social:
            empresaDoUsuario.razao_social ||
            dadosCompletos.razao_social ||
            dadosCompletos.nome_empresa ||
            "",
        };
      }
    } catch (_) {}
  }

  if (tipoProgExplicito === 0) {
    empresaDoUsuario.tipo_programa = 0;
    empresaDoUsuario._origem = "tce";
  } else if (tipoProgExplicito === 2) {
    empresaDoUsuario.tipo_programa = 2;
    empresaDoUsuario._origem = "tca-empregador";
  } else if (tipoProgExplicito === 1) {
    empresaDoUsuario.tipo_programa = 1;
    empresaDoUsuario._origem = "tca";
  }

  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      "empresa_id_acompanhamento",
      String(empresaDoUsuario.id),
    );
    window.localStorage.setItem(
      "empresa_busca_acompanhamento",
      montarDescricaoEmpresa(empresaDoUsuario),
    );
  }

  return empresaDoUsuario;
}

/**
 * Filtra e normaliza itens cadastrados por um consultor.
 */
export async function listarEmpresasDoConsultor({
  token,
  usuarioIdLogado,
  documentoLogado,
}) {
  const filtrar = (lista) =>
    lista
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean)
      .filter((item) =>
        empresaFoiCadastradaPeloConsultor(item, usuarioIdLogado, documentoLogado),
      );

  try {
    const listaPaginada = await listarEmpresasPaginadas({ token });
    return deduplicarAcompanhamentosPorEmpresa(
      filtrar(normalizarListaEmpresas(listaPaginada)),
    );
  } catch (error) {
    console.warn(
      "Falha na listagem paginada do consultor, tentando fallback simples:",
      error,
    );
  }

  try {
    const listaSimples = await listarEmpresasSimples(token);
    return deduplicarAcompanhamentosPorEmpresa(
      filtrar(normalizarListaEmpresas(listaSimples)),
    );
  } catch (error) {
    console.warn("Fallback simples do consultor também falhou:", error);
  }

  return [];
}

/**
 * Aplica filtros de role de acesso (CONSULTOR ou SUPEX).
 */
export function aplicarFiltroPorNivel(lista, nivel, identidade) {
  const ehCiee =
    identidade?.tipo === "usuario_ciee" ||
    [2, 3, 4].includes(Number(identidade?.acesso_autorizacao)) ||
    [2, 3, 4].includes(Number(identidade?.tipo_acesso)) ||
    [2, 3, 4].includes(Number(identidade?.nivel_acesso)) ||
    [2, 3, 4].includes(Number(identidade?.role_id)) ||
    [2, 3, 4].includes(Number(identidade?.nivel));
  if (ehCiee) return lista;

  if (nivel === NIVEL_CONSULTOR) {
    return lista.filter((item) =>
      empresaFoiCadastradaPeloConsultor(
        item,
        identidade.usuarioId,
        identidade.documento,
      ),
    );
  }
  if (nivel === NIVEL_SUPEX) {
    return lista.filter((item) => {
      const raw = item?.raw || item || {};
      return cadastroRequerSupex(raw, raw, [], item);
    });
  }
  return lista;
}

/**
 * Carrega acompanhamentos a partir da rota dedicada /acompanhamentos/me
 * com fallback para listagem geral de empresas e endpoints setoriais.
 */
export async function listarAcompanhamentosGenericos({
  token,
  nivel,
  identidade,
  busca,
  empresaId,
}) {
  const ehCieeOuAdmin =
    identidade?.tipo === "usuario_ciee" ||
    [2, 3, 4].includes(Number(nivel)) ||
    [2, 3, 4].includes(Number(identidade?.acesso_autorizacao)) ||
    [2, 3, 4].includes(Number(identidade?.tipo_acesso)) ||
    [2, 3, 4].includes(Number(identidade?.nivel_acesso)) ||
    [2, 3, 4].includes(Number(identidade?.role_id)) ||
    nivel === NIVEL_SUPEX;

  if (ehCieeOuAdmin) {
    let itensMe = [];
    try {
      const payload = await listarAcompanhamentosMe({
        token,
        empresaId,
        busca,
      });
      itensMe = normalizarListaEmpresas(payload)
        .map(normalizarEmpresaAcompanhamentoItem)
        .filter(Boolean);
    } catch (err) {
      console.warn("Aviso ao carregar /acompanhamentos/me para CIEE:", err?.message);
    }

    // Para CIEE / SUPEX / Admin: unifica as filas de validações e formulários em andamento
    // dos 3 programas (TCE Tipo 0, TCA Tipo 1 e TCA-Empregador Tipo 2) para visibilidade total.
    let itensFilas = [];
    const endpointsValidacoes = [
      { url: `${API_BASE_URL}/tce/pendentes-cieemg`, tp: 0, origem: "tce", etapas: 5 },
      { url: `${API_BASE_URL}/tce/validacoes/novos`, tp: 0, origem: "tce", etapas: 5 },
      { url: `${API_BASE_URL}/tce/validacoes/minhas`, tp: 0, origem: "tce", etapas: 5 },
      { url: `${API_BASE_URL}/tce`, tp: 0, origem: "tce", etapas: 5 },
      { url: `${API_BASE_URL}/tca/validacoes/novos`, tp: 1, origem: "tca", etapas: 4 },
      { url: `${API_BASE_URL}/tca/validacoes/minhas`, tp: 1, origem: "tca", etapas: 4 },
      { url: `${API_BASE_URL}/tca`, tp: 1, origem: "tca", etapas: 4 },
      { url: `${API_BASE_URL}/tca-empregador/validacoes/novos`, tp: 2, origem: "tca-empregador", etapas: 5 },
      { url: `${API_BASE_URL}/tca-empregador/validacoes/minhas`, tp: 2, origem: "tca-empregador", etapas: 5 },
      { url: `${API_BASE_URL}/tca-empregador`, tp: 2, origem: "tca-empregador", etapas: 5 },
    ];

    const params = {
      ...(empresaId ? { empresa_id: empresaId } : {}),
    };

    const resultadosValidacoes = await Promise.allSettled(
      endpointsValidacoes.map(async (ep) => {
        const resp = await axios.get(ep.url, {
          params,
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        return normalizarListaEmpresas(resp?.data).map((it) =>
          normalizarEmpresaAcompanhamentoItem({
            ...it,
            tipo_programa: ep.tp,
            _origem: ep.origem,
            total_etapas: ep.etapas,
            raw: { ...it, tipo_programa: ep.tp, _origem: ep.origem, total_etapas: ep.etapas },
          }),
        );
      }),
    );

    for (const res of resultadosValidacoes) {
      if (res.status === "fulfilled" && Array.isArray(res.value)) {
        itensFilas.push(...res.value.filter(Boolean));
      }
    }

    const todosUnificados = [...itensMe, ...itensFilas];

    // Se houver busca por ID numérico (ex: "109", "#109"), busca diretamente nos endpoints dos programas
    const termoIdLimpo = String(busca || "").replace(/\D/g, "");
    if (termoIdLimpo) {
      const jaContem = todosUnificados.some(
        (it) => String(extrairIdItem(it)) === termoIdLimpo,
      );
      if (!jaContem) {
        const endpointsId = [
          { url: `${API_BASE_URL}/tce/${termoIdLimpo}`, tp: 0, origem: "tce", etapas: 5 },
          { url: `${API_BASE_URL}/tca/${termoIdLimpo}`, tp: 1, origem: "tca", etapas: 4 },
          { url: `${API_BASE_URL}/tca-empregador/${termoIdLimpo}`, tp: 2, origem: "tca-empregador", etapas: 5 },
        ];
        const resId = await Promise.allSettled(
          endpointsId.map(async (ep) => {
            const resp = await axios.get(ep.url, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 10000,
            });
            if (resp?.data && (resp.data.id || resp.data.tce_id || resp.data.tca_id || resp.data.autorizacao_id)) {
              return normalizarEmpresaAcompanhamentoItem({
                ...resp.data,
                id: resp.data.id || termoIdLimpo,
                tipo_programa: resp.data.tipo_programa ?? ep.tp,
                _origem: resp.data._origem || ep.origem,
                total_etapas: ep.etapas,
                raw: { ...resp.data, tipo_programa: resp.data.tipo_programa ?? ep.tp, _origem: resp.data._origem || ep.origem, total_etapas: ep.etapas },
              });
            }
            return null;
          }),
        );
        for (const r of resId) {
          if (r.status === "fulfilled" && r.value) {
            todosUnificados.unshift(r.value);
          }
        }
      }
    }

    let listaFinal = deduplicarAcompanhamentosPorEmpresa(todosUnificados);

    if (busca) {
      listaFinal = listaFinal.filter((it) => itemCorrespondeAoTermo(it, busca));
    }

    // Perfil CIEE tem acesso total irrestrito: nunca é filtrado por consultor ou exclusivo de supex.
    return deduplicarAcompanhamentosPorEmpresa(listaFinal);
  }

  const tipoProgLogado = obterTipoProgramaUsuarioLogado();
  const isTceContext = tipoProgLogado === 0;

  try {
    const payload = await listarAcompanhamentosMe({
      token,
      empresaId,
      busca,
    });
    const lista = normalizarListaEmpresas(payload)
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean);

    if (isTceContext) {
      const listaTce = lista
        .filter((it) => itemPertenceAoPrograma(it, 0))
        .map((it) => ({
          ...it,
          tipo_programa: 0,
          _origem: "tce",
          total_etapas: 5,
          raw: { ...(it?.raw || it), tipo_programa: 0, _origem: "tce", total_etapas: 5 },
        }));

      if (listaTce.length > 0) {
        return deduplicarAcompanhamentosPorEmpresa(
          aplicarFiltroPorNivel(listaTce, nivel, identidade),
        );
      }
    } else {
      const tpEsperado = tipoProgLogado === 2 ? 2 : 1;
      const listaTca = lista
        .filter((it) => itemPertenceAoPrograma(it, tpEsperado))
        .map((it) => ({
          ...it,
          tipo_programa: tpEsperado,
          _origem: tpEsperado === 2 ? "tca-empregador" : "tca",
          total_etapas: tpEsperado === 2 ? 5 : 4,
          raw: {
            ...(it?.raw || it),
            tipo_programa: tpEsperado,
            _origem: tpEsperado === 2 ? "tca-empregador" : "tca",
            total_etapas: tpEsperado === 2 ? 5 : 4,
          },
        }));
      if (listaTca.length > 0) {
        return deduplicarAcompanhamentosPorEmpresa(
          aplicarFiltroPorNivel(listaTca, nivel, identidade),
        );
      }
    }
  } catch {
    /* tenta fallback setorial abaixo */
  }

  // Se o contexto for TCE, tenta listar contratos via endpoints exclusivos do TCE
  if (isTceContext) {
    const endpointsTce = [
      `${API_BASE_URL}/tce`,
      `${API_BASE_URL}/tce/estudantes-empresa`,
      `${API_BASE_URL}/tce/validacoes/novos`,
      `${API_BASE_URL}/tce/validacoes/minhas`,
      empresaId ? `${API_BASE_URL}/tce?empresa_id=${empresaId}` : null,
      empresaId ? `${API_BASE_URL}/tce/empresa/${empresaId}/autorizacao` : null,
      `${API_BASE_URL}/acompanhamentos/me`,
    ].filter(Boolean);

    let houveSucesso = false;
    let ultimoErro = null;
    for (const ep of endpointsTce) {
      try {
        const respTce = await axios.get(ep, {
          params: {
            ...(empresaId ? { empresa_id: empresaId } : {}),
            ...(busca ? { busca } : {}),
          },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        houveSucesso = true;
        const listaTce = normalizarListaEmpresas(respTce.data)
          .map((it) => ({
            ...it,
            tipo_programa: 0,
            _origem: "tce",
            total_etapas: 5,
            raw: { ...it, tipo_programa: 0, _origem: "tce", total_etapas: 5 },
          }))
          .filter((it) => itemPertenceAoPrograma(it, 0))
          .map(normalizarEmpresaAcompanhamentoItem)
          .filter(Boolean);
        if (listaTce.length > 0) {
          return deduplicarAcompanhamentosPorEmpresa(
            aplicarFiltroPorNivel(listaTce, nivel, identidade),
          );
        }
      } catch (err) {
        ultimoErro = err;
      }
    }
    if (!houveSucesso && ultimoErro) {
      throw ultimoErro;
    }
    return [];
  }

  // Se o contexto for TCA Empregador, tenta endpoints exclusivos do TCA Empregador
  if (tipoProgLogado === 2) {
    const endpointsTcaEmp = [
      `${API_BASE_URL}/tca-empregador`,
      `${API_BASE_URL}/tca-empregador/validacoes/novos`,
      `${API_BASE_URL}/tca-empregador/validacoes/minhas`,
      empresaId ? `${API_BASE_URL}/tca-empregador?empresa_id=${empresaId}` : null,
      empresaId ? `${API_BASE_URL}/tca-empregador/empresa/${empresaId}/autorizacao` : null,
      empresaId ? `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao` : null,
      `${API_BASE_URL}/acompanhamentos/me`,
    ].filter(Boolean);

    let houveSucesso = false;
    let ultimoErro = null;
    for (const ep of endpointsTcaEmp) {
      try {
        const respTcaEmp = await axios.get(ep, {
          params: {
            ...(empresaId ? { empresa_id: empresaId } : {}),
            ...(busca ? { busca } : {}),
            tipo_programa: 2,
            origem: "tca-empregador",
          },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        houveSucesso = true;
        const listaTcaEmp = normalizarListaEmpresas(respTcaEmp.data)
          .map((it) => ({
            ...it,
            tipo_programa: 2,
            _origem: "tca-empregador",
            total_etapas: 5,
            raw: { ...it, tipo_programa: 2, _origem: "tca-empregador", total_etapas: 5 },
          }))
          .filter((it) => itemPertenceAoPrograma(it, 2))
          .map(normalizarEmpresaAcompanhamentoItem)
          .filter(Boolean);
        if (listaTcaEmp.length > 0) {
          return deduplicarAcompanhamentosPorEmpresa(
            aplicarFiltroPorNivel(listaTcaEmp, nivel, identidade),
          );
        }
      } catch (err) {
        ultimoErro = err;
      }
    }
    if (!houveSucesso && ultimoErro) {
      throw ultimoErro;
    }
  }

  // Se o contexto for TCA Comum, tenta endpoints exclusivos do TCA
  if (tipoProgLogado === 1) {
    const endpointsTca = [
      `${API_BASE_URL}/tca`,
      `${API_BASE_URL}/tca/validacoes/novos`,
      `${API_BASE_URL}/tca/validacoes/minhas`,
      empresaId ? `${API_BASE_URL}/tca?empresa_id=${empresaId}` : null,
      empresaId ? `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao` : null,
      `${API_BASE_URL}/acompanhamentos/me`,
    ].filter(Boolean);

    let houveSucesso = false;
    let ultimoErro = null;
    for (const ep of endpointsTca) {
      try {
        const respTca = await axios.get(ep, {
          params: {
            ...(empresaId ? { empresa_id: empresaId } : {}),
            ...(busca ? { busca } : {}),
            tipo_programa: 1,
            origem: "tca",
          },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        houveSucesso = true;
        const listaTca = normalizarListaEmpresas(respTca.data)
          .map((it) => ({
            ...it,
            tipo_programa: 1,
            _origem: "tca",
            total_etapas: 4,
            raw: { ...it, tipo_programa: 1, _origem: "tca", total_etapas: 4 },
          }))
          .filter((it) => itemPertenceAoPrograma(it, 1))
          .map(normalizarEmpresaAcompanhamentoItem)
          .filter(Boolean);
        if (listaTca.length > 0) {
          return deduplicarAcompanhamentosPorEmpresa(
            aplicarFiltroPorNivel(listaTca, nivel, identidade),
          );
        }
      } catch (err) {
        ultimoErro = err;
      }
    }
    if (!houveSucesso && ultimoErro) {
      throw ultimoErro;
    }
  }

  let houveSucessoFallback = false;
  let ultimoErroFallback = null;
  try {
    const lista = await listarEmpresasPaginadas({
      token,
      busca,
      empresaId,
    });
    houveSucessoFallback = true;
    const itens = normalizarListaEmpresas(lista)
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean);
    return deduplicarAcompanhamentosPorEmpresa(
      aplicarFiltroPorNivel(itens, nivel, identidade),
    );
  } catch (err) {
    ultimoErroFallback = err;
    /* tenta fallback secundário */
  }

  try {
    const lista = await listarEmpresasSimples(token);
    houveSucessoFallback = true;
    const itens = normalizarListaEmpresas(lista)
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean);
    return deduplicarAcompanhamentosPorEmpresa(
      aplicarFiltroPorNivel(itens, nivel, identidade),
    );
  } catch (error) {
    ultimoErroFallback = error;
    console.warn("Fallback simples de empresas também falhou:", error);
  }

  if (!houveSucessoFallback && ultimoErroFallback) {
    throw ultimoErroFallback;
  }

  return [];
}

/**
 * Busca autorizações específicas de uma empresa logada conforme o tipo do programa ativo.
 */
export async function listarAcompanhamentosNivelEmpresa({
  token,
  unica,
  contexto = {},
  log = () => {},
}) {
  const tipoProgExplicito = obterTipoProgramaExplicitoUsuario();
  const tipoProg = tipoProgExplicito ?? obterTipoProgramaUsuarioLogado();
  const itensEmpresa = [];

  const filtrarEPadronizarLista = (listaAut, progPadrao, origemPadrao, etapasPadrao) => {
    const normalizados = listaAut
      .filter((aut) => {
        if (!aut || typeof aut !== "object") return false;
        // Se a empresa tem um programa explícito configurado, descarta contratos de outros programas
        // ANTES de preencher metadados padrão, preservando o isolamento por programa.
        const temMetadadoProprio =
          aut.tipo_programa !== undefined ||
          aut.tipoPrograma !== undefined ||
          aut._origem !== undefined ||
          aut.origem !== undefined ||
          aut.tce_id != null ||
          aut.tca_id != null ||
          aut.tca_empregador_id != null;
        if (tipoProgExplicito !== null && temMetadadoProprio) {
          if (!itemPertenceAoPrograma(aut, tipoProgExplicito)) return false;
        }

        const cnpjAut = resolverCnpjItem(aut).replace(/\D/g, "");
        const cnpjUnica = (unica?.cnpj || unica?.documento || "").replace(/\D/g, "");
        if (
          cnpjAut.length === 14 &&
          cnpjUnica.length === 14 &&
          cnpjAut.slice(0, 8) !== cnpjUnica.slice(0, 8)
        ) {
          return false;
        }
        return true;
      })
      .map((aut) => {
        const tpAut =
          aut.tipo_programa !== undefined && aut.tipo_programa !== null
            ? Number(aut.tipo_programa)
            : progPadrao;
        const origemAut = String(
          aut._origem ||
            (tpAut === 0 ? "tce" : tpAut === 1 ? "tca" : origemPadrao),
        )
          .toLowerCase()
          .replace(/_/g, "-");
        const etapasAut =
          aut.total_etapas || (tpAut === 1 ? 4 : etapasPadrao);
        const contractId =
          aut.id ??
          aut.tce_id ??
          aut.tca_empregador_id ??
          aut.tca_id ??
          aut.autorizacao_id;

        const baseItem = {
          ...unica,
          ...aut,
          id: contractId ?? aut.id ?? unica.id,
          ...(tpAut === 0 ? { tce_id: aut.tce_id ?? contractId ?? aut.id } : {}),
          ...(tpAut === 1 ? { tca_id: aut.tca_id ?? contractId ?? aut.id } : {}),
          ...(tpAut === 2
            ? {
                tca_id: aut.tca_id ?? contractId ?? aut.id,
                tca_empregador_id: aut.tca_empregador_id ?? contractId ?? aut.id,
              }
            : {}),
          empresa_id: aut.empresa_id ?? unica.id,
          tipo_programa: tpAut,
          _origem: origemAut,
          total_etapas: etapasAut,
        };

        return {
          ...baseItem,
          raw: {
            ...baseItem,
          },
        };
      });

    if (tipoProgExplicito !== null) {
      return normalizados.filter((it) => itemPertenceAoPrograma(it, tipoProgExplicito));
    }
    return normalizados;
  };

  // 1. TCE (tipoProg === 0)
  if (tipoProg === 0) {
    const endpointsTce = [
      `${API_BASE_URL}/acompanhamentos/me`,
      `${API_BASE_URL}/tce/empresa/${unica.id}/autorizacao`,
      `${API_BASE_URL}/tce`,
      `${API_BASE_URL}/tce/estudantes-empresa`,
      unica.cnpj || unica.documento
        ? `${API_BASE_URL}/tce?cnpj=${encodeURIComponent(unica.cnpj || unica.documento)}`
        : null,
    ].filter(Boolean);

    let houveSucesso = false;
    let ultimoErro = null;
    for (const ep of endpointsTce) {
      try {
        const respAut = await axios.get(ep, {
          params: {
            ...(ep.includes("?") ? {} : { empresa_id: unica.id }),
            ...(contexto?.busca ? { busca: contexto.busca } : {}),
            tipo_programa: 0,
            origem: "tce",
          },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        houveSucesso = true;
        const listaAut = extrairListaDeResposta(respAut?.data);
        if (listaAut.length > 0) {
          const tceFiltrada = filtrarEPadronizarLista(listaAut, 0, "tce", 5);
          if (tceFiltrada.length > 0) {
            itensEmpresa.push(...tceFiltrada);
            break;
          }
        }
      } catch (errAut) {
        ultimoErro = errAut;
        log("Tentativa em", ep, "falhou:", errAut?.message);
      }
    }

    if (itensEmpresa.length === 0 && !houveSucesso && ultimoErro) {
      throw ultimoErro;
    }

    if (itensEmpresa.length === 0 && unica?.formularios?.tce?.id) {
      itensEmpresa.push({
        ...unica,
        ...unica.formularios.tce,
        id: unica.formularios.tce.id,
        tce_id: unica.formularios.tce.id,
        tipo_programa: 0,
        _origem: "tce",
        total_etapas: 5,
        raw: {
          ...unica,
          ...unica.formularios.tce,
          tipo_programa: 0,
          _origem: "tce",
          total_etapas: 5,
        },
      });
    }
  }

  // 2. TCA Empregador (tipoProg === 2)
  if (tipoProg === 2) {
    const endpointsTcaEmp = [
      `${API_BASE_URL}/tca-empregador`,
      `${API_BASE_URL}/acompanhamentos/me`,
      `${API_BASE_URL}/tca-empregador/empresa/${unica.id}/autorizacao`,
      `${API_BASE_URL}/tca/empresa/${unica.id}/autorizacao`,
      unica.cnpj || unica.documento
        ? `${API_BASE_URL}/tca-empregador?cnpj=${encodeURIComponent(unica.cnpj || unica.documento)}`
        : null,
    ].filter(Boolean);

    let houveSucesso = false;
    let ultimoErro = null;
    for (const ep of endpointsTcaEmp) {
      try {
        const respAut = await axios.get(ep, {
          params: {
            ...(ep.includes("?") ? {} : { empresa_id: unica.id }),
            ...(contexto?.busca ? { busca: contexto.busca } : {}),
            tipo_programa: 2,
            origem: "tca-empregador",
          },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        houveSucesso = true;
        const listaAut = extrairListaDeResposta(respAut?.data);
        if (listaAut.length > 0) {
          const tcaEmpFiltrada = filtrarEPadronizarLista(
            listaAut,
            2,
            "tca-empregador",
            5,
          );
          if (tcaEmpFiltrada.length > 0) {
            itensEmpresa.push(...tcaEmpFiltrada);
            break;
          }
        }
      } catch (errAut) {
        ultimoErro = errAut;
        log("Tentativa em", ep, "falhou:", errAut?.message);
      }
    }

    if (itensEmpresa.length === 0 && !houveSucesso && ultimoErro) {
      throw ultimoErro;
    }

    if (
      itensEmpresa.length === 0 &&
      (unica?.formularios?.tca_empregador?.id || unica?.formularios?.tca?.id)
    ) {
      const formTca =
        unica.formularios.tca_empregador || unica.formularios.tca;
      itensEmpresa.push({
        ...unica,
        ...formTca,
        id: formTca.id,
        tca_id: formTca.id,
        tca_empregador_id: formTca.id,
        empresa_id: unica.id,
        tipo_programa: 2,
        _origem: "tca-empregador",
        total_etapas: 5,
        raw: {
          ...unica,
          ...formTca,
          id: formTca.id,
          tca_id: formTca.id,
          tca_empregador_id: formTca.id,
          empresa_id: unica.id,
          tipo_programa: 2,
          _origem: "tca-empregador",
          total_etapas: 5,
        },
      });
    }
  }

  // 3. TCA Comum (tipoProg === 1)
  if (tipoProg === 1) {
    const endpointsTca = [
      `${API_BASE_URL}/acompanhamentos/me`,
      `${API_BASE_URL}/tca/empresa/${unica.id}/autorizacao`,
      `${API_BASE_URL}/tca`,
      unica.cnpj || unica.documento
        ? `${API_BASE_URL}/tca?cnpj=${encodeURIComponent(unica.cnpj || unica.documento)}`
        : null,
    ].filter(Boolean);

    let houveSucesso = false;
    let ultimoErro = null;
    for (const ep of endpointsTca) {
      try {
        const respAut = await axios.get(ep, {
          params: {
            ...(ep.includes("?") ? {} : { empresa_id: unica.id }),
            ...(contexto?.busca ? { busca: contexto.busca } : {}),
            tipo_programa: 1,
            origem: "tca",
          },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        houveSucesso = true;
        const listaAut = extrairListaDeResposta(respAut?.data);
        if (listaAut.length > 0) {
          const tca1Filtrada = filtrarEPadronizarLista(listaAut, 1, "tca", 4);
          if (tca1Filtrada.length > 0) {
            itensEmpresa.push(...tca1Filtrada);
            break;
          }
        }
      } catch (errAut) {
        ultimoErro = errAut;
        log("Tentativa em", ep, "falhou:", errAut?.message);
      }
    }

    if (itensEmpresa.length === 0 && !houveSucesso && ultimoErro) {
      throw ultimoErro;
    }

    if (itensEmpresa.length === 0 && unica?.formularios?.tca?.id) {
      itensEmpresa.push({
        ...unica,
        ...unica.formularios.tca,
        id: unica.formularios.tca.id,
        tca_id: unica.formularios.tca.id,
        empresa_id: unica.id,
        tipo_programa: 1,
        _origem: "tca",
        total_etapas: 4,
        raw: {
          ...unica,
          ...unica.formularios.tca,
          id: unica.formularios.tca.id,
          tca_id: unica.formularios.tca.id,
          empresa_id: unica.id,
          tipo_programa: 1,
          _origem: "tca",
          total_etapas: 4,
        },
      });
    }
  }

  // Se houver busca por ID numérico (ex: "109", "#109"), busca diretamente no endpoint do contrato
  const termoIdLimpo = String(contexto?.busca || "").replace(/\D/g, "");
  if (termoIdLimpo) {
    const jaContem = itensEmpresa.some(
      (it) => String(extrairIdItem(it)) === termoIdLimpo,
    );
    if (!jaContem) {
      const endpointsId = [
        { url: `${API_BASE_URL}/tce/${termoIdLimpo}`, tp: 0, origem: "tce", etapas: 5 },
        { url: `${API_BASE_URL}/tca/${termoIdLimpo}`, tp: 1, origem: "tca", etapas: 4 },
        { url: `${API_BASE_URL}/tca-empregador/${termoIdLimpo}`, tp: 2, origem: "tca-empregador", etapas: 5 },
      ].filter((ep) => tipoProgExplicito === null || ep.tp === tipoProgExplicito);
      const resId = await Promise.allSettled(
        endpointsId.map(async (ep) => {
          const resp = await axios.get(ep.url, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000,
          });
          if (resp?.data && (resp.data.id || resp.data.tce_id || resp.data.tca_id || resp.data.autorizacao_id)) {
            return normalizarEmpresaAcompanhamentoItem({
              ...unica,
              ...resp.data,
              id: resp.data.id || termoIdLimpo,
              tipo_programa: resp.data.tipo_programa ?? ep.tp,
              _origem: resp.data._origem || ep.origem,
              total_etapas: ep.etapas,
              raw: { ...unica, ...resp.data, tipo_programa: resp.data.tipo_programa ?? ep.tp, _origem: resp.data._origem || ep.origem, total_etapas: ep.etapas },
            });
          }
          return null;
        }),
      );
      for (const r of resId) {
        if (r.status === "fulfilled" && r.value) {
          itensEmpresa.unshift(r.value);
        }
      }
    }
  }

  if (itensEmpresa.length === 0) {
    return [];
  }

  return deduplicarAcompanhamentosPorEmpresa(
    itensEmpresa.map(normalizarEmpresaAcompanhamentoItem).filter(Boolean),
  );
}
